import type {
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ThemeService } from '@freelancer/theme';
import {
  flFileDarkmode,
  flFileDarkmodeAvi,
  flFileDarkmodeDoc,
  flFileDarkmodeFlv,
  flFileDarkmodeGif,
  flFileDarkmodeJpg,
  flFileDarkmodeKey,
  flFileDarkmodeMov,
  flFileDarkmodeMp4,
  flFileDarkmodeMpg,
  flFileDarkmodePdf,
  flFileDarkmodePng,
  flFileDarkmodePps,
  flFileDarkmodeRar,
  flFileDarkmodeRtf,
  flFileDarkmodeSvg,
  flFileDarkmodeTxt,
  flFileDarkmodeXls,
  flFileDarkmodeZip,
  flFileLightmode,
  flFileLightmodeAvi,
  flFileLightmodeDoc,
  flFileLightmodeFlv,
  flFileLightmodeGif,
  flFileLightmodeJpg,
  flFileLightmodeKey,
  flFileLightmodeMov,
  flFileLightmodeMp4,
  flFileLightmodeMpg,
  flFileLightmodePdf,
  flFileLightmodePng,
  flFileLightmodePps,
  flFileLightmodeRar,
  flFileLightmodeRtf,
  flFileLightmodeSvg,
  flFileLightmodeTxt,
  flFileLightmodeXls,
  flFileLightmodeZip,
  flFileWhite,
  flFileWhiteAudio,
  flFileWhiteCode,
  flFileWhiteImg,
  flFileWhiteVideo,
  IconColor,
  IconModule,
  IconName,
  iconNameToConstName,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import {
  PictureComponent,
  PictureLayout,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { isDefined } from '@freelancer/utils';
import { Subscription } from 'rxjs';
import {
  FileDisplayIconType,
  FileDisplaySize,
  FileDisplayType,
  FILE_DISPLAY_SIZE_TO_PIXELS_MAPPING,
} from './file-display.types';

@Component({
  standalone: true,
  imports: [IconModule, PictureComponent],
  selector: 'fl-file-display',
  template: `
    @switch (type) {
      @case (FileDisplayType.THUMBNAIL) {
        @if (isImage) {
          <fl-picture
            class="FileDisplay Image"
            [alt]="alt"
            [attr.data-size]="size"
            [height]="FILE_DISPLAY_SIZE_TO_PIXELS_MAPPING[size]"
            [layout]="PictureLayout.FIXED"
            [objectFit]="PictureObjectFit.COVER"
            [resizeFit]="'crop'"
            [src]="src"
            [width]="FILE_DISPLAY_SIZE_TO_PIXELS_MAPPING[size]"
            (error)="onImageError.emit()"
          />
        } @else {
          <fl-icon
            class="FileDisplay Icon"
            [class.IsLarge]="size !== FileDisplaySize.XSMALL"
            [size]="IconSize.MID"
            [name]="iconName"
            [label]="label"
          />
        }
      }

      @case (FileDisplayType.ICON) {
        <fl-icon
          [color]="iconColor"
          [label]="label"
          [name]="iconName"
          [size]="iconSize"
          [sizeTablet]="iconSizeTablet"
          [sizeDesktop]="iconSizeDesktop"
        />
      }
    }
  `,
  styleUrls: ['./file-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideFlIcons({
      flFileDarkmode,
      flFileDarkmodeAvi,
      flFileDarkmodeDoc,
      flFileDarkmodeFlv,
      flFileDarkmodeGif,
      flFileDarkmodeJpg,
      flFileDarkmodeKey,
      flFileDarkmodeMov,
      flFileDarkmodeMp4,
      flFileDarkmodeMpg,
      flFileDarkmodePdf,
      flFileDarkmodePng,
      flFileDarkmodePps,
      flFileDarkmodeRar,
      flFileDarkmodeRtf,
      flFileDarkmodeSvg,
      flFileDarkmodeTxt,
      flFileDarkmodeXls,
      flFileDarkmodeZip,
      flFileLightmode,
      flFileLightmodeAvi,
      flFileLightmodeDoc,
      flFileLightmodeFlv,
      flFileLightmodeGif,
      flFileLightmodeJpg,
      flFileLightmodeKey,
      flFileLightmodeMov,
      flFileLightmodeMp4,
      flFileLightmodeMpg,
      flFileLightmodePdf,
      flFileLightmodePng,
      flFileLightmodePps,
      flFileLightmodeRar,
      flFileLightmodeRtf,
      flFileLightmodeSvg,
      flFileLightmodeTxt,
      flFileLightmodeXls,
      flFileLightmodeZip,
      flFileWhite,
      flFileWhiteAudio,
      flFileWhiteCode,
      flFileWhiteImg,
      flFileWhiteVideo,
    }),
  ],
})
export class FileDisplayComponent implements OnInit, OnChanges, OnDestroy {
  uiConfig = inject<UiConfig>(UI_CONFIG);

  FileDisplaySize = FileDisplaySize;
  FileDisplayType = FileDisplayType;
  IconSize = IconSize;
  PictureLayout = PictureLayout;
  PictureObjectFit = PictureObjectFit;
  FILE_DISPLAY_SIZE_TO_PIXELS_MAPPING = FILE_DISPLAY_SIZE_TO_PIXELS_MAPPING;
  private subscription = new Subscription();
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);

  @Input() alt: string;
  /** Overrides the identifying of filetype from src, which is used for src that does not have file extension (e.g. new file service download URL) */
  @Input() filename?: string;
  @Input() iconColor = IconColor.FOREGROUND;
  @Input() iconSize = IconSize.MID;
  @Input() iconSizeTablet?: IconSize;
  @Input() iconSizeDesktop?: IconSize;
  @Input() src: string;

  /** Use file specific or generic icon per file type */
  @Input() iconType = FileDisplayIconType.SPECIFIC;

  @HostBinding('attr.data-type')
  @Input()
  type = FileDisplayType.THUMBNAIL;

  // Thumbnail size
  @HostBinding('attr.data-size')
  @Input()
  size = FileDisplaySize.SMALL;

  /**
   * Shows a checkerboard pattern behind image thumbnails to indicate
   * transparency. Has no effect on opaque images, so it's on by default; set
   * to `false` to opt out and keep a solid background.
   */
  @HostBinding('attr.data-transparent-bg')
  @Input()
  transparentBackground = true;

  @Output() onImageError = new EventEmitter<void>();

  iconName: IconName;
  @HostBinding('attr.data-is-image')
  isImage: boolean;
  fileType: string;
  label: string;
  currentTheme = 'light';

  async ngOnInit(): Promise<void> {
    this.subscription.add(
      this.themeService.getTheme().subscribe(async theme => {
        this.currentTheme = theme;
        this.updateIconName();
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      ('filename' in changes && isDefined(this.filename)) ||
      ('src' in changes && isDefined(this.src))
    ) {
      // Get filetype from filename if it exists, else try to get it from src
      const fileTypeBasis = this.filename || this.src;
      const fileparts = fileTypeBasis.split('.');
      const lastFilePart = fileparts.at(-1);

      this.fileType = lastFilePart
        ? lastFilePart.toLowerCase().split('?')[0]
        : '';

      this.isImage = this.isImageFile();
      this.updateIconName();
      this.label = this.getIconLabel();
    }
  }

  private updateIconName(): void {
    if (this.iconType === FileDisplayIconType.SPECIFIC) {
      this.iconName = iconNameToConstName(this.getFileIcon()) as IconName;
    } else if (this.iconType === FileDisplayIconType.GENERIC) {
      this.iconName = this.getTypeIcon();
    }
  }

  getFileIcon(): IconName {
    const themePrefix = this.currentTheme === 'dark' ? 'darkmode' : 'lightmode';
    switch (this.fileType) {
      case 'avi':
        return `ui-file-${themePrefix}-avi`;
      case 'doc':
      case 'docx':
        return `ui-file-${themePrefix}-doc`;
      case 'flv':
        return `ui-file-${themePrefix}-flv`;
      case 'gif':
        return `ui-file-${themePrefix}-gif`;
      case 'jpg':
      case 'jpeg':
        return `ui-file-${themePrefix}-jpg`;
      case 'key':
        return `ui-file-${themePrefix}-key`;
      case 'mp4':
        return `ui-file-${themePrefix}-mp4`;
      case 'mov':
        return `ui-file-${themePrefix}-mov`;
      case 'mpg':
        return `ui-file-${themePrefix}-mpg`;
      case 'pdf':
        return `ui-file-${themePrefix}-pdf`;
      case 'png':
        return `ui-file-${themePrefix}-png`;
      case 'pps':
      case 'ppt':
      case 'pptx':
        return `ui-file-${themePrefix}-pps`;
      case 'rar':
        return `ui-file-${themePrefix}-rar`;
      case 'rtf':
        return `ui-file-${themePrefix}-rtf`;
      case 'svg':
        return `ui-file-${themePrefix}-svg`;
      case 'txt':
        return `ui-file-${themePrefix}-txt`;
      case 'xls':
      case 'xlsx':
        return `ui-file-${themePrefix}-xls`;
      case 'zip':
        return `ui-file-${themePrefix}-zip`;
      default:
        return `ui-file-${themePrefix}`;
    }
  }

  isAudioFile(): boolean {
    return ['aac', 'ogg', 'mp3', 'wma', 'wav'].includes(this.fileType);
  }

  isCodeFile(): boolean {
    return ['html', 'js', 'ts', 'java', 'php', 'py', 'xml'].includes(
      this.fileType,
    );
  }

  isImageFile(): boolean {
    // these are the image files that can be viewed on all browsers we support
    const supportedImageFile = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
    const imageDataURLRegex =
      /^data:image\/(?:jpg|jpeg|png|gif|bmp|svg)(;base64)?,[a-zA-Z0-9!$&',()*+;=\-._~:@/?%]*$/i;
    return (
      supportedImageFile.includes(this.fileType) ||
      !!this.src?.match(imageDataURLRegex)
    );
  }

  isVideoFile(): boolean {
    return ['avi', 'flv', 'mov', 'mpg', 'mp4'].includes(this.fileType);
  }

  /**
   * Map icon based on file type
   */
  getTypeIcon(): IconName {
    if (this.isAudioFile()) {
      return 'flFileWhiteAudio';
    }

    if (this.isCodeFile()) {
      return 'flFileWhiteCode';
    }

    if (this.isImageFile()) {
      return 'flFileWhiteImg';
    }

    if (this.isVideoFile()) {
      return 'flFileWhiteVideo';
    }

    return 'flFileWhite';
  }

  /**
   * Map label based on file type
   */
  getIconLabel(): string {
    if (this.isAudioFile()) {
      return $localize`Audio File Icon`;
    }

    if (this.isCodeFile()) {
      return $localize`Code File Icon`;
    }

    if (this.isImageFile()) {
      return $localize`Image File Icon`;
    }

    if (this.isVideoFile()) {
      return $localize`Video File Icon`;
    }

    return $localize`File Icon`;
  }

  /**
   * Map icon based on file extension
   */

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
